import { Button, Stack, TextField } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import {} from 'react-router'
import { useMutation } from 'urql'
import { graphql } from '../../gql'
import { OrderContextProviderQuery } from '../../gql/graphql'
import { formatFormInputCurrencyString } from '../../utils/currencyFormatter'

const updateQuoteMutation = graphql(`
  mutation EditQuoteFormUpdateQuote($id: Int!, $input: UpdateQuoteInput!) {
    UpdateQuote(id: $id, input: $input) {
      id
    }
  }
`)
type TProps = {
  quote: NonNullable<OrderContextProviderQuery['Rfq']['quote']>
  onCancel: () => void
  onUpdated: () => void
}
export function EditQuoteForm(props: TProps) {
  const { quote, onUpdated, onCancel } = props
  const [updateQuoteResult, updateQuote] = useMutation(updateQuoteMutation)

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    if (updateQuoteResult.fetching) return
    const input = {
      amount: Number.parseFloat(data.amount.replace(/[^0-9.]/g, '')),
      dateSent: data.dateSent?.toISOString(),
      designFirm: data.designFirm,
      quoteNumber: data.quoteNumber,
    }
    const resp = await updateQuote({
      id: quote.id,
      input,
    })
    if (!resp.error) {
      onUpdated()
    }
  }

  interface IFormInput {
    quoteNumber: string
    amount: string
    dateSent: Dayjs | undefined
    designFirm: string
  }

  const { handleSubmit, control, setValue } = useForm<IFormInput>({
    defaultValues: {
      amount: formatFormInputCurrencyString(quote.amount.toString()),
      dateSent: quote.dateSent ? dayjs(quote.dateSent) : dayjs(),
      quoteNumber: quote.quoteNumber || '',
      designFirm: quote.designFirm || '',
    },
    mode: 'onBlur',
    disabled: updateQuoteResult.fetching,
  })

  const handleAmountBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const formatted = formatFormInputCurrencyString(e.target.value)
    if (!formatted) return
    setValue('amount', formatted)
  }

  return (
    <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)} onReset={onCancel}>
      <Stack sx={{ ml: 0, mt: 2 }} spacing={4} justifyContent="stretch">
        <Controller
          name="amount"
          control={control}
          rules={{
            required: true,
            validate: (v) => {
              if (!v) return 'Amount is required'
              const n = Number.parseFloat(v as any as string)
              if (Number.isNaN(n)) {
                return 'Amount must be a number'
              }
              return true
            },
            onBlur: handleAmountBlur,
          }}
          render={({ field, fieldState }) => {
            return (
              <TextField
                label="Amount ($) *"
                {...field}
                // disabled={fetching}
                error={!!fieldState.error}
                helperText={fieldState.error?.message || 'The total amount of the purchase order'}
                slotProps={{
                  input: {
                    startAdornment: '$',
                    required: true,
                  },
                }}
              />
            )
          }}
        />
        <Controller
          name="quoteNumber"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <TextField
                label="Quote Number"
                {...field}
                // disabled={fetching}
                error={!!fieldState.error}
                helperText={fieldState.error?.message || undefined}
              />
            )
          }}
        />
        <Controller
          name="dateSent"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <DatePicker
                label="Date Sent"
                {...field}
                // disabled={fetching}
                disableFuture
                slotProps={{
                  textField: {
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message || undefined,
                  },
                  field: {
                    clearable: true,
                  },
                }}
              />
            )
          }}
        />
        <Controller
          name="designFirm"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <TextField
                label="Design Firm"
                {...field}
                // disabled={fetching}
                error={!!fieldState.error}
                helperText={fieldState.error?.message || undefined}
              />
            )
          }}
        />

        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
          <Button type="reset" variant="outlined" color="primary">
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="secondary" disabled={updateQuoteResult.fetching}>
            Submit
          </Button>
        </Stack>
      </Stack>
    </form>
  )
}
